import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../components/AppIcon';
import PaymentDetailModal from '../../components/PaymentDetailModal';
import { useToast } from '../../context/ToastContext';
import { fetchAllPayments, fetchFailedPayments } from '../../lib/adminApi';
import { exportToCsv, downloadFilename } from '../../lib/csvExport';

const paymentStatusFilters = [
  { value: 'all', label: 'All Payments' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
];

const statusStyles = {
  completed: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-rose-100 text-rose-700',
  processing: 'bg-blue-100 text-blue-700',
};

const paymentExportColumns = [
  { key: 'payment_reference', label: 'Ref' },
  { key: 'gross_amount', label: 'Amount' },
  { key: 'payment_status', label: 'Status' },
  { key: 'payment_method', label: 'Method' },
  { key: 'customer_email', label: 'Customer' },
  { key: 'caterer_name', label: 'Caterer' },
  { key: 'order_id', label: 'Order ID' },
  { key: 'created_at', label: 'Date' },
];

export default function PaymentsPage() {
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('all');
  const [payments, setPayments] = useState([]);
  const [failedPayments, setFailedPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [detailPaymentId, setDetailPaymentId] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function loadPayments() {
      setLoading(true);
      setError(null);

      try {
        if (activeTab === 'failed') {
          const data = await fetchFailedPayments(100);
          if (isMounted) {
            setFailedPayments(data);
            setPayments([]);
          }
        } else {
          const data = await fetchAllPayments({ status: statusFilter === 'all' ? undefined : statusFilter });
          if (isMounted) {
            setPayments(data);
            setFailedPayments([]);
          }
        }
      } catch (err) {
        console.error('Failed to load payments', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load payments');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadPayments();

    return () => {
      isMounted = false;
    };
  }, [statusFilter, activeTab]);

  const displayPayments = activeTab === 'failed' ? failedPayments : payments;
  const totalAmount = displayPayments.reduce((sum, p) => sum + (Number(p.gross_amount) || 0), 0);
  const failedCount = failedPayments.length;
  const failedAmount = failedPayments.reduce((sum, p) => sum + (Number(p.gross_amount) || 0), 0);

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Payment Management</h2>
          <p className="text-sm text-muted-foreground">
            Monitor all payment transactions, track failed payments, and manage payment issues.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              const list = activeTab === 'failed' ? failedPayments : payments;
              const rows = list.map((p) => ({
                payment_reference: p.payment_reference ?? '',
                gross_amount: p.gross_amount,
                payment_status: p.payment_status ?? '',
                payment_method: p.payment_method ?? '',
                customer_email: p.customer?.email ?? '',
                caterer_name: p.caterer?.business_name ?? '',
                order_id: p.order?.id ?? '',
                created_at: p.created_at ? format(new Date(p.created_at), 'yyyy-MM-dd HH:mm') : '',
              }));
              if (!rows.length) {
                toast.info('No payments to export');
                return;
              }
              exportToCsv(rows, paymentExportColumns, downloadFilename(activeTab === 'failed' ? 'payments-failed' : 'payments'));
              toast.success(`Exported ${rows.length} payments`);
            }}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium text-foreground shadow-sm hover:bg-muted"
          >
            <Icon name="Download" size={16} /> Export
          </button>
        </div>
      </header>

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Payments</p>
              <p className="mt-2 text-2xl font-semibold text-foreground">{payments.length}</p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon name="CreditCard" size={22} />
            </span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Amount</p>
              <p className="mt-2 text-2xl font-semibold text-foreground">R {totalAmount.toLocaleString()}</p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
              <Icon name="Wallet" size={22} />
            </span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Failed Payments</p>
              <p className="mt-2 text-2xl font-semibold text-foreground text-rose-600">{failedCount}</p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-rose-100 text-rose-600">
              <Icon name="AlertCircle" size={22} />
            </span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Failed Amount</p>
              <p className="mt-2 text-2xl font-semibold text-foreground text-rose-600">R {failedAmount.toLocaleString()}</p>
            </div>
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-rose-100 text-rose-600">
              <Icon name="TrendingDown" size={22} />
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'all'
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          All Payments
        </button>
        <button
          onClick={() => setActiveTab('failed')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'failed'
              ? 'border-b-2 border-rose-600 text-rose-600'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Failed Payments ({failedCount})
        </button>
      </div>

      {/* Payments Table */}
      <div className="rounded-xl border border-border bg-card shadow-sm">
        {activeTab === 'all' && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border/80 px-4 py-4">
            {paymentStatusFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setStatusFilter(filter.value)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  statusFilter === filter.value
                    ? 'bg-primary text-primary-foreground shadow'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-muted/30 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Payment Ref</th>
                <th className="px-4 py-3 text-left font-semibold">Order</th>
                <th className="px-4 py-3 text-left font-semibold">Customer</th>
                <th className="px-4 py-3 text-left font-semibold">Caterer</th>
                <th className="px-4 py-3 text-left font-semibold">Amount</th>
                <th className="px-4 py-3 text-left font-semibold">Method</th>
                <th className="px-4 py-3 text-left font-semibold">Status</th>
                <th className="px-4 py-3 text-left font-semibold">Date</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/80">
              {loading && (
                <tr>
                  <td colSpan={9} className="px-6 py-6 text-center text-muted-foreground">
                    Loading payments…
                  </td>
                </tr>
              )}

              {!loading && displayPayments.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-6 text-center text-muted-foreground">
                    No payments found.
                  </td>
                </tr>
              )}

              {displayPayments.map((payment) => (
                <tr key={payment.id} className="hover:bg-muted/30">
                  <td className="px-4 py-3 font-mono text-xs text-foreground">{payment.payment_reference?.slice(0, 12)}...</td>
                  <td className="px-4 py-3 font-medium text-foreground">{payment.order?.id?.slice(0, 8)}...</td>
                  <td className="px-4 py-3 text-muted-foreground">{payment.customer?.email || '—'}</td>
                  <td className="px-4 py-3 text-muted-foreground">{payment.caterer?.business_name || '—'}</td>
                  <td className="px-4 py-3 font-semibold text-foreground">
                    R {Number(payment.gross_amount || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground capitalize">{payment.payment_method || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                      statusStyles[payment.payment_status] ?? 'bg-muted text-foreground'
                    }`}>
                      {payment.payment_status || 'unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {payment.created_at ? format(new Date(payment.created_at), 'dd MMM yyyy HH:mm') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setDetailPaymentId(payment.id)}
                      className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1 text-xs font-semibold text-muted-foreground hover:bg-muted"
                    >
                      <Icon name="Eye" size={14} /> View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detailPaymentId && (
        <PaymentDetailModal paymentId={detailPaymentId} onClose={() => setDetailPaymentId(null)} />
      )}
    </div>
  );
}
